import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation,
  Outlet,
} from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { Auth0ContextProvider } from "@/contexts/Auth0Context";
import { Toaster } from "@/components/ui/toaster";
import { Layout } from "@/components/layout/Layout";
import { Upload } from "@/pages/Upload";
import { Landing } from "@/pages/Landing";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Documents from "@/pages/Documents";
import Review from "@/pages/Review";
import { Settings } from "@/pages/Settings";
import { Billing } from "@/pages/Billing";
import { Profile } from "@/pages/Profile";
import { Documentation } from "@/pages/Documentation";
import { Careers } from "@/pages/Careers";
import { Privacy } from "@/pages/Privacy";
import { Terms } from "@/pages/Terms";
import { Contact } from "@/pages/Contact";
import { AzureMarketplaceLanding } from "@/pages/AzureMarketplaceLanding";
import { MarketplaceSuccess } from "@/pages/MarketplaceSuccess";
import { auth0Config } from "@/config/auth0";
import { useEffect } from "react";

// Component to debug routing and Auth0 callback
function AppContent() {
  const location = useLocation();

  useEffect(() => {
    console.log("Route changed:", {
      pathname: location.pathname,
      search: location.search,
      isCallback:
        location.search.includes("code=") || location.search.includes("state="),
      fullUrl: window.location.href,
    });
  }, [location]);

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-1 flex flex-col">
        <Outlet />
        <Toaster />
      </div>
    </div>
  );
}

// Protected App Layout Component
function ProtectedAppLayout() {
  return (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  );
}

// Create the router with future flags
const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <AppContent />,
      children: [
        {
          // Public routes
          index: true,
          element: <Landing />,
        },
        {
          path: "docs",
          element: <Documentation />,
        },
        {
          path: "careers",
          element: <Careers />,
        },
        {
          path: "privacy",
          element: <Privacy />,
        },
        {
          path: "terms",
          element: <Terms />,
        },
        {
          path: "contact",
          element: <Contact />,
        },
        {
          // Azure Marketplace routes
          path: "azuremarketplace",
          children: [
            {
              // Handle base /azuremarketplace/ path
              index: true,
              element: <AzureMarketplaceLanding />,
            },
            {
              path: "landing",
              element: <AzureMarketplaceLanding />,
            },
            {
              path: "link",
              element: <AzureMarketplaceLanding />,
            },
            {
              path: "success",
              element: <MarketplaceSuccess />,
            },
          ],
        },
        {
          // Protected routes
          path: "app",
          element: <ProtectedAppLayout />,
          children: [
            {
              // Redirect /app to /app/upload
              index: true,
              element: <Navigate to="/app/upload" replace />,
            },
            {
              path: "upload",
              element: <Upload />,
            },
            {
              path: "documents",
              element: <Documents />,
            },
            {
              path: "review",
              element: <Review />,
            },
            {
              path: "settings",
              element: <Settings />,
            },
            {
              path: "billing",
              element: <Billing />,
            },
            {
              path: "profile",
              element: <Profile />,
            },
          ],
        },
        {
          // Catch all route - redirect to landing
          path: "*",
          element: <Navigate to="/" replace />,
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
);

function App() {
  return (
    <HelmetProvider>
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={auth0Config.authorizationParams}
        cacheLocation={auth0Config.cacheLocation}
        useRefreshTokens={auth0Config.useRefreshTokens}
      >
        <Auth0ContextProvider>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </Auth0ContextProvider>
      </Auth0Provider>
    </HelmetProvider>
  );
}

export default App;
